import { useEffect, useRef, type ReactNode } from 'react';

export function Menu() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <div className="vazio">
      <span className="spinner" style={{ borderColor: 'var(--border-strong)', borderTopColor: 'var(--accent)' }} />
      <p>{texto}</p>
    </div>
  );
}

/**
 * Lista vazia não é erro, e por isso não usa a cor de erro. Mas também não
 * pode ser uma tela em branco: sem dizer o que fazer, quem chega aqui
 * acha que o sistema quebrou.
 */
export function Vazio({ titulo, descricao, acao }: { titulo: string; descricao?: string; acao?: ReactNode }) {
  return (
    <div className="vazio">
      <h3>{titulo}</h3>
      {descricao && <p>{descricao}</p>}
      {acao}
    </div>
  );
}

export function Erro({ children }: { children: ReactNode }) {
  return children ? <p className="error-msg">{children}</p> : null;
}

export function Campo({
  rotulo, ajuda, children,
}: { rotulo: string; ajuda?: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label className="field-label">{rotulo}</label>
      {children}
      {ajuda && <p className="campo-ajuda">{ajuda}</p>}
    </div>
  );
}

export function Modal({
  titulo, descricao, onFechar, children, largura = 520,
}: {
  titulo: string;
  descricao?: string;
  onFechar: () => void;
  children: ReactNode;
  largura?: number;
}) {
  const caixa = useRef<HTMLDivElement>(null);

  // Esc fecha. Modal que só fecha no X prende quem abriu por engano — e
  // aqui quase todo modal é um formulário de cadastro, onde abrir por
  // engano é comum.
  useEffect(() => {
    function tecla(e: KeyboardEvent) { if (e.key === 'Escape') onFechar(); }
    document.addEventListener('keydown', tecla);
    caixa.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    return () => document.removeEventListener('keydown', tecla);
  }, [onFechar]);

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="modal" style={{ maxWidth: largura }} ref={caixa} role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="modal-topo">
          <div>
            <h2>{titulo}</h2>
            {descricao && <p>{descricao}</p>}
          </div>
          <button className="modal-x" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const ESTADOS: Record<string, { texto: string; classe: string }> = {
  ok: { texto: 'Em dia', classe: 'ok' },
  calado: { texto: 'Sem contato há 24h', classe: 'atencao' },
  nunca: { texto: 'Nunca falou', classe: 'neutro' },
  auto: { texto: 'Cadastrada pelo XML', classe: 'info' },
  manual: { texto: 'Cadastrada à mão', classe: 'neutro' },
  ativo: { texto: 'Ativo', classe: 'ok' },
  inativo: { texto: 'Inativo', classe: 'neutro' },
  atencao: { texto: 'Atenção', classe: 'atencao' },
  info: { texto: 'Info', classe: 'info' },
};

export function Selo({ tipo, texto }: { tipo: string; texto?: string }) {
  const e = ESTADOS[tipo] || { texto: tipo, classe: 'neutro' };
  return <span className={`selo ${e.classe}`}>{texto || e.texto}</span>;
}

/** Datas relativas: "há 3 minutos" diz mais que um carimbo, e é o que o
 *  suporte lê primeiro para saber se um agente parou. */
export function quando(iso: string | null): string {
  if (!iso) return 'nunca';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora mesmo';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `há ${d} dia${d > 1 ? 's' : ''}`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

// Escritorio de contador autonomo guarda CPF na mesma coluna que o CNPJ --
// formata pelo tamanho, nao pelo contexto de quem chama.
export function cnpjFormatado(cnpj: string): string {
  const d = (cnpj || '').replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export function mascaraCelular(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function mascaraCnpj(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

// Escritorio de contador autonomo nao tem CNPJ: ate 11 digitos formata como
// CPF, dai pra frente vira CNPJ -- a pessoa digitando nao escolhe o tipo.
export function mascaraCpfCnpj(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
  }
  return mascaraCnpj(d);
}

export interface Coluna<T> {
  /** Cabeçalho no computador e etiqueta de cada linha no celular. */
  rotulo: string;
  celula: (item: T) => ReactNode;
  /** A coluna que identifica a linha. Vira o título do cartão, sem etiqueta. */
  principal?: boolean;
  /** Número: alinhado à direita e com dígitos de largura fixa. */
  numero?: boolean;
  /** Botões da linha. Vão para o rodapé do cartão no celular. */
  acoes?: boolean;
  /** Texto secundário — some no cartão para não competir com o que importa. */
  fraco?: boolean;
  mono?: boolean;
}

/**
 * Tabela que vira lista de cartões no celular.
 *
 * Existe como componente, e não como classe de CSS, porque a virada
 * depende de cada célula carregar a sua etiqueta (`data-rotulo`) — e isso
 * é exatamente o tipo de coisa que se esquece na próxima tabela. Aqui a
 * etiqueta sai da mesma definição que desenha o cabeçalho: não há como
 * escrever uma tabela nova que role para o lado no celular.
 */
export function Tabela<T>({
  colunas, itens, chaveDe, classeLinha,
}: {
  colunas: Coluna<T>[];
  itens: T[];
  chaveDe: (item: T) => string | number;
  classeLinha?: (item: T) => string | undefined;
}) {
  const classeDa = (c: Coluna<T>) => [
    c.principal ? 'td-principal' : '',
    c.numero ? 'td-numero' : '',
    c.acoes ? 'td-acoes td-direita' : '',
    c.fraco ? 'td-fraco' : '',
    c.mono ? 'td-mono' : '',
  ].filter(Boolean).join(' ') || undefined;

  return (
    <table className="tbl">
      <thead>
        <tr>
          {colunas.map((c) => (
            <th key={c.rotulo} className={c.numero ? 'td-numero' : undefined}>
              {c.acoes ? '' : c.rotulo}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {itens.map((item) => (
          <tr key={chaveDe(item)} className={classeLinha?.(item)}>
            {colunas.map((c) => (
              <td key={c.rotulo} data-rotulo={c.rotulo} className={classeDa(c)}>
                {c.celula(item)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
